import { Info, Pencil, Trash2, Users } from "lucide-react";
import React, { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate } from "react-router";
import { RequireScope } from "src/components/RequireScope";
import { ScopeGate } from "src/components/ScopeGate";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "src/components/ui/hover-card";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";

import { SettingsPageHeader } from "../components/SettingsPageHeader";
import { useSettingsMobileHeader } from "../hooks/useSettingsMobileHeader";
import { TeamsRouteDeleteTeamMutation } from "./__generated__/TeamsRouteDeleteTeamMutation.graphql";
import { TeamsRouteQuery } from "./__generated__/TeamsRouteQuery.graphql";

const teamsRouteQuery = graphql`
  query TeamsRouteQuery {
    me {
      ...appMe
      id
      teamContext {
        id
        team {
          id
          name
        }
      }
      organization {
        id
        teams {
          edges {
            node {
              id
              name
              description
              isDeletable
            }
          }
        }
      }
    }
  }
`;

const deleteTeamMutation = graphql`
  mutation TeamsRouteDeleteTeamMutation($input: DeleteTeamInput!) {
    deleteTeam(input: $input) {
      success
      error
    }
  }
`;

type TeamType = NonNullable<NonNullable<NonNullable<NonNullable<TeamsRouteQuery["response"]["me"]>["organization"]>["teams"]["edges"][0]>["node"]>;

export default function TeamsRoute() {
  return (
    <RequireScope scope="read:teams">
      <TeamsRouteContent />
    </RequireScope>
  );
}

function TeamsRouteContent() {
  useSettingsMobileHeader("Teams");
  const navigate = useNavigate();
  const data = useLazyLoadQuery<TeamsRouteQuery>(teamsRouteQuery, {});
  const [commitDeleteTeam] = useMutation<TeamsRouteDeleteTeamMutation>(deleteTeamMutation);
  const { canWrite } = usePermissions();
  const canModify = canWrite("teams");

  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamType | null>(null);

  // Get current team ID from team context
  const currentTeamId = data.me?.teamContext?.team?.id;

  const teams = (data.me?.organization?.teams?.edges?.map(edge => edge.node) || [])
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleDeleteTeam = () => {
    if (!selectedTeam) return;

    commitDeleteTeam({
      variables: {
        input: {
          id: selectedTeam.id,
        },
      },
      updater: (store, data) => {
        if (!data || !data.deleteTeam?.success) return;
        const root = store.getRoot();
        const me = root.getLinkedRecord("me");
        const organization = me?.getLinkedRecord("organization");
        const teams = organization?.getLinkedRecord("teams");
        const edges = teams?.getLinkedRecords("edges") || [];

        const filteredEdges = edges.filter(edge => {
          const node = edge?.getLinkedRecord("node");
          return node?.getValue("id") !== selectedTeam.id;
        });

        if (teams) {
          teams.setLinkedRecords(filteredEdges, "edges");
        }
      },
      onCompleted: (response) => {
        if (response.deleteTeam?.success) {
          toast({
            title: "Success",
            description: "Team deleted successfully",
          });
          setShowDeleteDialog(false);
          setSelectedTeam(null);
        } else {
          toast({
            title: "Error",
            description: response.deleteTeam?.error || "Failed to delete team",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  const openDeleteDialog = (team: TeamType) => {
    setSelectedTeam(team);
    setShowDeleteDialog(true);
  };

  return (
    <>
      <div className="flex-1 px-4 md:px-5 pt-4 pb-2">
        <SettingsPageHeader
          title="Teams"
          tooltip="Manage teams within your organization."
        >
          <ScopeGate scope="write:teams">
            <Button
              onClick={() => navigate("/settings/teams/new")}
              size="sm"
            >
              Create Team
            </Button>
          </ScopeGate>
        </SettingsPageHeader>
      </div>
      <div className="px-4 md:px-5 pb-5 pt-0 md:pt-4">

        {!canModify && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4">
            <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-primary">
                You have read-only access to teams. Contact an admin to make changes.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {teams.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 border border-border/70 rounded-lg bg-card/20">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center mb-4">
                No teams found. {canModify ? "Create your first team to get started." : "Contact an admin to create teams."}
              </p>
              <ScopeGate scope="write:teams">
                <Button
                  onClick={() => navigate("/settings/teams/new")}
                  variant="secondary"
                  size="sm"
                >
                  Create Team
                </Button>
              </ScopeGate>
            </div>
          ) : (
            teams.map((team) => (
              <div
                key={team.id}
                className="flex items-center justify-between px-4 py-2.5 border border-border/70 rounded-lg bg-card/30 hover:bg-card/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-7 h-7 rounded-md bg-accent/50 flex-shrink-0">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center">
                      <span className="text-[15px] font-medium text-foreground">{team.name}</span>
                      {team.id === currentTeamId && (
                        <span className="ml-2 text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                    {team.description && (
                      <span className="text-sm text-muted-foreground line-clamp-1">{team.description}</span>
                    )}
                  </div>
                </div>
                <ScopeGate scope="write:teams">
                  <div className="flex items-center gap-1">
                    <Tooltip content="Edit team" position="top">
                      <button
                        className="p-2 hover:bg-accent rounded-lg transition-colors"
                        onClick={() => navigate(`/settings/teams/${team.id}/edit`)}
                      >
                        <Pencil className="h-4 w-4 text-muted-foreground hover:text-primary" />
                      </button>
                    </Tooltip>
                    {!team.isDeletable || team.id === currentTeamId ? (
                      <HoverCard openDelay={0} closeDelay={100}>
                        <HoverCardTrigger asChild>
                          <button
                            className="p-2 rounded-lg cursor-not-allowed"
                            onClick={(e) => e.preventDefault()}
                          >
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80">
                          <p className="text-sm">
                            {team.id === currentTeamId
                              ? "Cannot delete this team because you have currently selected this team. Please switch to another team first."
                              : "Cannot delete this team because it has connections to other resources."}
                          </p>
                        </HoverCardContent>
                      </HoverCard>
                    ) : (
                      <Tooltip content="Delete team" position="top">
                        <button
                          className="p-2 hover:bg-accent rounded-lg transition-colors"
                          onClick={() => openDeleteDialog(team)}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-400" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </ScopeGate>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete Team Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedTeam?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setSelectedTeam(null);
            }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTeam}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
